(ns ui.view.card.counterparty-diligence-progress.sub
  (:require
   [re-frame.core :as rf]
   [ui.view.card.sub :as common-sub]
   [ui.view.card.counterparty-diligence-progress.model :as model]
   [common.schema.entities.counterparty-diligence.process :as process-schema]
   [clojure.string :as string]
   [common.util :as cu]
   [common.localization :as loc]))

(rf/reg-sub
 ::change-status-confirmation-modal-open?
 common-sub/card-state-extractor
 (fn [[state]]
   (get-in state model/change-status-confirmation-modal-open-path)))

(rf/reg-sub
 ::change-to-status
 common-sub/card-state-extractor
 (fn [[state]]
   (let [status (get-in state model/change-to-status-path)
         label (process-schema/get-status-label status)]
     {:value status
      :label label})))

(rf/reg-sub
 ::counterparty-diligence
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/entities ctx])])
 (fn [[entities] [_ {:keys [entity-id]}]]
   (get entities entity-id)))

(rf/reg-sub
 ::counterparty-diligence-process
 (fn [[_ ctx]]
   [(rf/subscribe [::counterparty-diligence ctx])
    (rf/subscribe [::common-sub/entities ctx])])
 (fn [[counterparty-diligence entities]]
   (->> counterparty-diligence
        :counterparty-diligence/processes
        (mapv #(get entities %))
        (sort-by :common/created-on)
        last)))

(rf/reg-sub
 ::process-status
 (fn [[_ ctx]]
   [(rf/subscribe [::counterparty-diligence-process ctx])])
 (fn [[process]]
   (:counterparty-diligence-process/status process)))

(rf/reg-sub
 ::process-answers
 (fn [[_ ctx]]
   [(rf/subscribe [::counterparty-diligence-process ctx])])
 (fn [[process]]
   (:counterparty-diligence-process/answers process)))

(rf/reg-sub
 ::signatories-from-answers
 (fn [[_ ctx]]
   [(rf/subscribe [::process-answers ctx])])
 (fn [[answers]]
   (:signatories answers)))

(rf/reg-sub
 ::signatory-error
 (fn [[_ ctx]]
   [(rf/subscribe [::signatories-from-answers ctx])])
 (fn [[signatories]]
   (cond
     (empty? signatories)
     :signatory-error/no-signatories

     (->> signatories
          (some (fn [{:keys [full-name email]}]
                  (let [full-name (-> full-name
                                      (or "")
                                      string/trim)
                        email (-> email
                                  (or "")
                                  string/trim)]
                    (or (cu/blank-value? full-name)
                        (cu/blank-value? email))))))
     :signatory-error/data-missing

     :else nil)))

(rf/reg-sub
 ::can-submit-status-change?
 (fn [[_ ctx]]
   [(rf/subscribe [::process-status ctx])
    (rf/subscribe [::change-to-status ctx])
    (rf/subscribe [::signatory-error ctx])])
 (fn [[current-status new-status-opt signatory-error]]
   (let [{new-status :value} new-status-opt]
     (cond
       (= current-status :counterparty-diligence-process.status/completed)
       false

       (and (= new-status :counterparty-diligence-process.status/waiting-for-e-sign)
            signatory-error)
       false

       :else true))))

(rf/reg-sub
 ::submitting-status-change?
 common-sub/card-state-extractor
 (fn [[state]]
   (get-in state model/submitting-status-change-path)))

(rf/reg-sub
 ::change-status-error-modal-open?
 common-sub/card-state-extractor
 (fn [[state]]
   (get-in state model/change-status-error-modal-open-path)))

(rf/reg-sub
 ::change-status-error-kw
 common-sub/card-state-extractor
 (fn [[state]]
   (get-in state model/change-status-error-kw-path)))

(rf/reg-sub
 ::e-signature-required?
 (fn [[_ ctx]]
   [(rf/subscribe [::counterparty-diligence ctx])])
 (fn [[counterparty-diligence]]
   (-> counterparty-diligence
       :counterparty-diligence/e-signature-required
       (= :yes))))

(rf/reg-sub
 ::esign-process
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/entities ctx])])
 (fn [[entities-by-id]]
   (->> entities-by-id
        vals
        (cu/only-entity :entity.type/esign-process)
        (sort-by :common/created-on)
        last)))

(rf/reg-sub
 ::esign-process-signed?
 (fn [[_ ctx]]
   [(rf/subscribe [::esign-process ctx])])
 (fn [[esign-process]]
   (= (:esign-process/status esign-process) :esign-process.status/signed)))

(rf/reg-sub
 ::esign-process-failed?
 (fn [[_ ctx]]
   [(rf/subscribe [::esign-process ctx])])
 (fn [[esign-process]]
   (= (:esign-process/status esign-process) :esign-process.status/failed)))

(defn resolve-step-data
  [{:keys [process-status step-index-by-value
           counterparty-diligence
           counterparty-diligence-process
           esign-process]} status]
  (let [current-process-status (:counterparty-diligence-process/status counterparty-diligence-process)
        label (process-schema/get-status-label status)
        step-index (get step-index-by-value status)

        current-step-index (get step-index-by-value current-process-status)
        step-status (cond
                      (= current-step-index step-index) :current
                      (< current-step-index step-index) :upcoming
                      :else :completed)

        e-signature-required? (-> counterparty-diligence
                                  :counterparty-diligence/e-signature-required
                                  (= :yes))

        esign-process-status (:esign-process/status esign-process)
        esign-process-signed? (= esign-process-status :esign-process.status/signed)
        esign-process-failed? (= esign-process-status :esign-process.status/failed)

        responders? (-> counterparty-diligence-process
                        :counterparty-diligence-process/responders
                        seq)
        signatories (-> counterparty-diligence-process
                        :counterparty-diligence-process/answers
                        :signatories)

        signatory-data-missing? (when e-signature-required?
                                  (or (empty? signatories)
                                      (->> signatories
                                           (some (fn [{:keys [full-name email]}]
                                                   (let [full-name (string/trim (or full-name ""))
                                                         email (string/trim (or email ""))]
                                                     (or (cu/blank-value? full-name)
                                                         (cu/blank-value? email))))))))

        disabled? (or (= process-status status)

                      (= process-status :counterparty-diligence-process.status/completed)

                      (= status :counterparty-diligence-process.status/review)

                      (and (= status :counterparty-diligence-process.status/sent-to-responders)
                           (not responders?))

                      (and (= status :counterparty-diligence-process.status/waiting-for-e-sign)
                           signatory-data-missing?)

                      (and (= status :counterparty-diligence-process.status/waiting-for-e-sign)
                           (some #{process-status} [:counterparty-diligence-process.status/review
                                                    :counterparty-diligence-process.status/completed]))

                      (and (= process-status :counterparty-diligence-process.status/waiting-for-e-sign)
                           (some #{status} [:counterparty-diligence-process.status/review
                                            :counterparty-diligence-process.status/completed]))

                      (and (= status :counterparty-diligence-process.status/completed)
                           e-signature-required?
                           (not esign-process-signed?)))

        error? (and (= status :counterparty-diligence-process.status/waiting-for-e-sign)
                    esign-process-failed?)

        tooltip (cond
                  (and (= status :counterparty-diligence-process.status/waiting-for-e-sign)
                       esign-process-failed?)
                  (loc/tr :e-signature-process-failed)

                  (and (= status :counterparty-diligence-process.status/completed)
                       e-signature-required?
                       (not esign-process-signed?))
                  (loc/tr :e-signature-required)

                  (and (= status :counterparty-diligence-process.status/sent-to-responders)
                       (not responders?))
                  (loc/tr :add-at-least-one-responder-first)

                  (and (= status :counterparty-diligence-process.status/waiting-for-e-sign)
                       signatory-data-missing?)

                  (loc/tr :signatories-are-missing-data)

                  :else nil)]
    {:value status
     :label label
     :step-status step-status
     :disabled? disabled?
     :error? error?
     :tooltip tooltip}))

(rf/reg-sub
 ::status-steps
 (fn [[_ ctx]]
   [(rf/subscribe [::counterparty-diligence ctx])
    (rf/subscribe [::counterparty-diligence-process ctx])
    (rf/subscribe [::e-signature-required? ctx])
    (rf/subscribe [::esign-process ctx])
    (rf/subscribe [::process-status ctx])
    (rf/subscribe [::esign-process-signed? ctx])
    (rf/subscribe [::esign-process-failed? ctx])])
 (fn [[counterparty-diligence
       counterparty-diligence-process
       e-signature-required?
       esign-process
       process-status
       _esign-process-signed?
       _esign-process-failed?]]
   (let [responders? (->> counterparty-diligence
                          :counterparty-diligence/responder
                          seq)

         process-status-steps
         (concat
          [:counterparty-diligence-process.status/draft]

          (when (or responders?
                    e-signature-required?
                    (= process-status :counterparty-diligence-process.status/sent-to-responders))
            [:counterparty-diligence-process.status/sent-to-responders])

          (when (or e-signature-required?
                    (= process-status :counterparty-diligence-process.status/waiting-for-e-sign))
            [:counterparty-diligence-process.status/waiting-for-e-sign])

          (when (or responders?
                    e-signature-required?
                    (= process-status :counterparty-diligence-process.status/review))
            [:counterparty-diligence-process.status/review])

          [:counterparty-diligence-process.status/completed])

         step-index-by-value (->> process-status-steps
                                  (map-indexed (fn [index value]
                                                 [value index]))
                                  (into {}))

         current-step-index (get step-index-by-value process-status)]
     (->> process-status-steps
          (mapv (partial resolve-step-data {:process-status process-status
                                            :current-step-index current-step-index
                                            :step-index-by-value step-index-by-value
                                            :e-signature-required? e-signature-required?
                                            :esign-process esign-process
                                            :counterparty-diligence counterparty-diligence
                                            :counterparty-diligence-process counterparty-diligence-process}))))))
